import type { BatchStatus, BatchSummary, Item, ItemBatch, Warehouse } from '@erp/shared';
import { CalendarClock, PackagePlus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { Badge, Button, Combobox, type ComboboxOption, confirmDialog, DataTable, type DataTableColumn, FormField, Input } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

const STATUS_TONE: Record<BatchStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success', expiring: 'warning', expired: 'danger', none: 'neutral',
};

export function BatchesPage(): JSX.Element {
  const { t } = useTranslation('batches');
  const canWrite = usePermission('stock.adjust');
  const [summary, setSummary] = useState<BatchSummary | null>(null);
  const [rows, setRows] = useState<ItemBatch[] | null>(null);
  const [filter, setFilter] = useState<'' | BatchStatus>('');
  const [items, setItems] = useState<Item[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // new-batch form
  const [itemId, setItemId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = (f: '' | BatchStatus = filter): void => {
    void api().get<{ items: ItemBatch[] }>('/batches', { params: f ? { status: f } : {} }).then((r) => setRows(r.data.items));
    void api().get<BatchSummary>('/batches/summary').then((r) => setSummary(r.data));
  };
  useEffect(() => {
    reload('');
    // Whole catalogue, paged — a bare /items caps at the server default (200)
    // and the picker would silently miss most of a big shop's items.
    void (async () => {
      const all: Item[] = [];
      for (let offset = 0; offset < 10_000; offset += 500) {
        const r = await api().get<{ items: Item[] }>('/items', { params: { limit: 500, offset } });
        all.push(...r.data.items);
        if (r.data.items.length < 500) break;
      }
      setItems(all.filter((i) => i.isActive));
    })();
    void api().get<{ items: Warehouse[] }>('/warehouses').then((r) => setWarehouses(r.data.items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilter = (f: '' | BatchStatus): void => { setFilter(f); reload(f); };

  const itemOptions: ComboboxOption[] = useMemo(() => items.map((i) => ({ value: i.id, label: `${i.code} · ${i.name}`, keywords: i.code })), [items]);
  const warehouseOptions: ComboboxOption[] = useMemo(() => warehouses.map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` })), [warehouses]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!itemId || !batchNumber.trim() || Number(quantity) < 0 || quantity.trim() === '' || busy) return;
    setBusy(true);
    try {
      await api().post('/batches', {
        itemId,
        warehouseId: warehouseId || null,
        batchNumber: batchNumber.trim(),
        expiryDate: expiryDate || null,
        quantity: quantity.trim(),
      });
      toast.success(t('created', { defaultValue: 'Batch added.' }));
      setBatchNumber(''); setExpiryDate(''); setQuantity('');
      reload();
    } catch { toast.error(t('createFailed', { defaultValue: 'Could not add the batch.' })); } finally { setBusy(false); }
  };

  const remove = async (b: ItemBatch): Promise<void> => {
    if (!(await confirmDialog({ title: t('deleteConfirm', { defaultValue: 'Delete this batch?' }), message: `${b.itemName} · ${b.batchNumber}`, tone: 'danger', confirmLabel: t('delete', { defaultValue: 'Delete' }) }))) return;
    try { await api().delete(`/batches/${b.id}`); reload(); }
    catch { toast.error(t('deleteFailed', { defaultValue: 'Could not delete the batch.' })); }
  };

  if (!rows || !summary) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;

  const expiryCell = (b: ItemBatch): JSX.Element => {
    if (!b.expiryDate) return <span className="text-fg-subtle">—</span>;
    const label = b.status === 'expired'
      ? t('expiredAgo', { defaultValue: '{{days}}d ago', days: Math.abs(b.daysToExpiry ?? 0) })
      : t('inDays', { defaultValue: 'in {{days}}d', days: b.daysToExpiry ?? 0 });
    return <span className="inline-flex items-center gap-2"><span className="font-mono text-xs">{b.expiryDate}</span><Badge tone={STATUS_TONE[b.status]}>{label}</Badge></span>;
  };

  const columns: DataTableColumn<ItemBatch>[] = [
    { id: 'item', header: t('columns.item', { defaultValue: 'Item' }), sortValue: (b) => b.itemCode, searchValue: (b) => `${b.itemCode} ${b.itemName} ${b.batchNumber}`, cell: (b) => <><span className="font-mono text-xs">{b.itemCode}</span> · {b.itemName}</> },
    { id: 'batch', header: t('columns.batch', { defaultValue: 'Batch' }), sortValue: (b) => b.batchNumber, cell: (b) => <span className="font-mono text-xs">{b.batchNumber}</span> },
    { id: 'warehouse', header: t('columns.warehouse', { defaultValue: 'Warehouse' }), sortValue: (b) => b.warehouseName ?? '', cell: (b) => b.warehouseName ?? '—' },
    { id: 'expiry', header: t('columns.expiry', { defaultValue: 'Expiry' }), sortValue: (b) => b.expiryDate ?? '9999', cell: expiryCell },
    { id: 'quantity', header: t('columns.quantity', { defaultValue: 'Qty' }), align: 'end', sortValue: (b) => Number(b.quantity), cellClassName: 'font-mono tabular-nums', cell: (b) => Number(b.quantity) },
    ...(canWrite ? [{ id: 'actions', header: '', align: 'end' as const, cell: (b: ItemBatch) => (
      <button type="button" onClick={() => void remove(b)} className="text-fg-muted transition hover:text-danger" aria-label={t('delete', { defaultValue: 'Delete' })}><Trash2 className="h-4 w-4" aria-hidden /></button>
    ) }] : []),
  ];

  const chip = (f: '' | BatchStatus, label: string): JSX.Element => (
    <button type="button" onClick={() => applyFilter(f)} className={`rounded-full px-3 py-1 text-sm font-medium transition ${filter === f ? 'bg-brand text-brand-contrast' : 'bg-bg-subtle text-fg-muted hover:text-fg'}`}>{label}</button>
  );

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><CalendarClock className="h-6 w-6 text-brand" aria-hidden />{t('title', { defaultValue: 'Batches & expiry' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Track stock lots and their expiry dates. Sorted soonest-to-expire first (FEFO).' })}</p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card className="p-4"><div className="text-sm text-fg-muted">{t('summary.expired', { defaultValue: 'Expired' })}</div><div className="mt-1 text-2xl font-bold text-danger">{summary.expiredCount}</div></Card>
        <Card className="p-4"><div className="text-sm text-fg-muted">{t('summary.expiring', { defaultValue: 'Expiring ≤{{days}}d', days: summary.expiringWindowDays })}</div><div className="mt-1 text-2xl font-bold text-warning">{summary.expiringCount}</div></Card>
        <Card className="p-4"><div className="text-sm text-fg-muted">{t('summary.total', { defaultValue: 'Total batches' })}</div><div className="mt-1 text-2xl font-bold">{summary.totalCount}</div></Card>
      </div>

      {canWrite && (
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-semibold">{t('newBatch', { defaultValue: 'New batch' })}</h2>
          <form className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 lg:items-end" onSubmit={submit}>
            <div className="lg:col-span-2"><FormField id="b-item" label={t('item', { defaultValue: 'Item' })}><Combobox value={itemId} onChange={(v) => setItemId(v ?? '')} options={itemOptions} placeholder={t('selectItem', { defaultValue: 'Select item' })} /></FormField></div>
            <FormField id="b-wh" label={t('warehouse', { defaultValue: 'Warehouse' })}><Combobox value={warehouseId} onChange={(v) => setWarehouseId(v ?? '')} options={warehouseOptions} placeholder={t('any', { defaultValue: 'Any' })} /></FormField>
            <FormField id="b-num" label={t('batchNumber', { defaultValue: 'Batch / lot #' })}><Input id="b-num" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} maxLength={64} /></FormField>
            <FormField id="b-exp" label={t('expiry', { defaultValue: 'Expiry date' })}><Input id="b-exp" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} /></FormField>
            <div className="flex items-end gap-2">
              <FormField id="b-qty" label={t('quantity', { defaultValue: 'Qty' })}><Input id="b-qty" type="number" inputMode="decimal" min="0" step="0.0001" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="text-end font-mono" /></FormField>
              <Button type="submit" loading={busy} disabled={!itemId || !batchNumber.trim() || quantity.trim() === ''}><PackagePlus className="h-4 w-4" aria-hidden /></Button>
            </div>
          </form>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {chip('', t('filter.all', { defaultValue: 'All' }))}
        {chip('expiring', t('filter.expiring', { defaultValue: 'Expiring' }))}
        {chip('expired', t('filter.expired', { defaultValue: 'Expired' }))}
      </div>

      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={rows} rowKey={(b) => b.id} />
      </Card>
    </div>
  );
}
